
import ChannelContext from '@jetshop/core/components/ChannelContext';
import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';
import { useTouchpoint } from './useTouchPoint';
import { elevate, localStorageBackedSession } from '@apptus/esales-api';
import { useHistory } from 'react-router';
import { ElevateCustomerHandler } from './ElevateCustomerHandler';
import { useFavoriteStore } from '../../contexts/FavoriteStoreContext';
import {
  CUSTOMER_KEY_ACTION,
  resolveCustomerKeyAction
} from './customerKeySync';
import { logElevateCustomerKey } from './elevateCustomerKeyLog';
import { CUSTOM_STORE_FACET_KEYS } from './facets/types/CustomStoreFacet';



/** TODO 
 * List for things that have to be implemented:
 * - Handle search origins
 * - Handle tracking (clicked and viewed items, page views, etc.)
 * - Handle customfields
 * */

const ElevateContext = createContext(null);

export const ELEVATE_SEARCH_ORIGINS = {
  organic: 'ORGANIC', // Indicates a standard user-initiated search. The user types a query in the search bar and presses Enter.
  didYouMean: 'DID_YOU_MEAN', // Indicates that the user clicked a Did you mean suggestion returned in the search-page response. The search response includes “Did you mean: sneakers”, and the user clicks it.
  undoAutoCorrect: 'UNDO_AUTO_CORRECT', //Indicates that the user chose to revert an automatic correction made by Elevate. The user types “iphon”, Elevate corrects it to “iphone”, but the user clicks the original phrase “iphon”.
  phraseSuggest: 'PHRASE_SUGGEST', //Indicates that the user selected a suggested phrase from autocomplete. While typing “snea”, autocomplete suggests “sneakers adidas”, and the user clicks it.
  relatedSearches: 'RELATED_SEARCHES', //Indicates that the user clicked a related search phrase displayed in a previous search response. The search results for “running shoes” include a related search “trail shoes”, and the user clicks it.
  recentSearch: 'RECENT_SEARCH', //Indicates that the user selected a recent search phrase from autocomplete. In the autocomplete recent searches section, the user clicks a previous query.
}

const RECOMMENDATION_ALGORITHMS = {
  addToCartRecs: 'ADD_TO_CART_RECS',
  alternatives: 'ALTERNATIVES',
  cart: 'CART',
  favorites: 'FAVORITES',
  moreFromSeries: 'MORE_FROM_SERIES',
  newestProducts: 'NEWEST_PRODUCTS',
  personal: 'PERSONAL',
  recentlyViewed: 'RECENTLY_VIEWED',
  styleWith: 'STYLE_WITH',
  topProducts: 'TOP_PRODUCTS',
  upsell: 'UPSELL',
};

const NOTIFICATION_TYPES = {
 click: 'click',
 addToCart: 'addToCart',
 addToFavorites: 'addFavorite',
 removeFromFavorites: 'removeFavorite',
}

const ElevateDefaultSettings = {
  PER_PAGE: 24,
  NOTIFY: false,
  CUSTOMFIELDS: 'color | json.badgedata | badges | marketingCategories',
  RECOMMENDATIONS_LIMIT: 12
}

// Elevate `price` rules use the active market's selling price, not a shared currency.
// Keep these in the same cheap-addon band (~300 SEK).
const CART_REC_MAX_PRICE = {
  SEK: 150,
  NOK: 150,
  DKK: 100,
  EUR: 15
};

const cartRecProductRules = currency => {
  const max =
    CART_REC_MAX_PRICE[currency] ?? CART_REC_MAX_PRICE.SEK;
  return `rule incl price [0, ${max}]`;
};

const baseSettings = {
  presentPrices: ['MEMBER','MEMBER_SE','MEMBER_NO','MEMBER_DK','MEMBER_FI','MEMBER_NL'],
  presentCustom: ElevateDefaultSettings.CUSTOMFIELDS,

}


export const ElevateProvider = ({ children }) => {
  const history = useHistory();
  const { selectedChannel } = useContext(ChannelContext);
  const market = selectedChannel?.country?.code;
  const locale = selectedChannel?.language?.culture;
  const touchpoint = useTouchpoint();

  const { favoriteStore, getAllStores, storesLoading } = useFavoriteStore();

  const [listFacetStoreId, setListFacetStoreId] = useState(null);

  const elevateStoreIds = useMemo(() => {

    const stores = getAllStores();
    if (!stores.length) {
      return undefined;
    }

    return stores
      .map(store => store.id)
      .filter(Boolean)
      .join('|');
  }, [favoriteStore?.id, storesLoading, selectedChannel?.name]); // eslint-disable-line react-hooks/exhaustive-deps

  const elevateStoresParam = useMemo(
    () => (elevateStoreIds ? { stores: elevateStoreIds } : {}),
    [elevateStoreIds]
  );

  const elevateSession = useMemo(() => {
    if (typeof window === 'undefined') return null;
    return localStorageBackedSession();
  }, []);

  const elevateApi = useMemo(() => {
    if (!elevateSession) return null;

    return elevate({
      clusterId: process.env.REACT_APP_ELEVATE_CLUSTER_ID,
      market: selectedChannel?.language?.culture?.split('-')[1] ?? 'SE',
      locale: selectedChannel?.language?.culture ?? 'sv-SE',
      touchpoint, // must be 'desktop' | 'mobile'
      session: () => elevateSession()
    });
  }, [market, locale, touchpoint, elevateSession]);

const generateElevateListParams = (rest) => {
const {
      [CUSTOM_STORE_FACET_KEYS.STORE]: storeFacet,
      [CUSTOM_STORE_FACET_KEYS.ONLINE]: onlineFacet,
      ["f.onlyInStock"]: onlyInStock,
      ...elevateRest
    } = rest;

  // Presence in URL = user/default decision. Absent = let Elevate's facet default apply.
  const onlineDecided = Object.prototype.hasOwnProperty.call(
    rest,
    CUSTOM_STORE_FACET_KEYS.ONLINE
  );
  const storeDecided = Object.prototype.hasOwnProperty.call(
    rest,
    CUSTOM_STORE_FACET_KEYS.STORE
  );
  const onlineOn =
    onlineDecided && onlineFacet !== false && onlineFacet !== 'false';
  const onlyInStockValue = onlineOn || !!storeFacet;

    const storesParams = {}
    if (storeFacet) {
      storesParams.stores = storeFacet;
      setListFacetStoreId(storeFacet);
    } else {
      storesParams.stores = elevateStoresParam?.stores;
      setListFacetStoreId(null);
    }
    const channels = [
      onlineOn && 'ONLINE',
      storeFacet && 'STORE'
    ]
      .filter(Boolean)
      .join('|');

  return {
    ...elevateRest,
    ...(onlineDecided || storeDecided
      ? { 'f.onlyInStock': onlyInStockValue }
      : {}),
    ...storesParams,
    ...(channels ? { channels } : {})
  }
}

  const getLandingPageData = async ({pageReference, limit = ElevateDefaultSettings.PER_PAGE, skip = 0, page, ...rest}) => {
    if (!elevateApi) {
      // SSR / missing elevate init: keep Elevate optional
      return null;
    }

    const elevateListParams = generateElevateListParams(rest);

    const skipQuantity = page ? (page - 1) * limit : skip;
    
    const landingPageResponse = await elevateApi.query.landingPage({
      limit,
      skip: skipQuantity,
      ...baseSettings,
      pageReference: pageReference,
      ...elevateListParams,
      
    });

   
    return landingPageResponse;
  };

  
  const getLandingPageRecommendationsData = async ({pageReference, listId, algorithm = RECOMMENDATION_ALGORITHMS.topProducts, ...rest}) => {
      if (!elevateApi) {
        // SSR / missing elevate init: keep Elevate optional
        return null;
      }
  
      const landingPageResponse = await elevateApi.query.landingPage({
        ...baseSettings,
        pageReference: pageReference,
        ...rest,
        ...elevateStoresParam
      },
    {
        recommendationLists: [
          {
            id: listId,
            algorithm: algorithm
          }
        ]
      });
    return landingPageResponse;
  };

  const getAutoCompleteData = async ({searchTerm, origin = ELEVATE_SEARCH_ORIGINS.organic, ...rest}) => {

    if (!elevateApi) {
      // SSR / missing elevate init: keep Elevate optional
      return null;
    }
    // Always notify (Ola Axelsson 2026-05-29)
    // let shouldNotify = true;
    // // Only notify if the search term is at least 3 characters long
    // if (searchTerm?.length < 3) {
    //   shouldNotify = false;
    // }

    const autoCompleteResponse = await elevateApi.query.autocomplete({
      q: searchTerm,
      ...baseSettings,
      origin,
      ...rest,
      ...elevateStoresParam
    },
    {
      contentLists: [{ id: 'all-content',limit: 15 }]
    });
    
    return autoCompleteResponse;
  };

  const getSearchPageData = async ({searchTerm, origin = ELEVATE_SEARCH_ORIGINS.organic, limit = ElevateDefaultSettings.PER_PAGE, skip = 0, page, ...rest}) => {
    if (!elevateApi) {
      // SSR / missing elevate init: keep Elevate optional
      return null;
    }
     let shouldNotify = true;
    // Only notify if the search term is at least 3 characters long
    if (searchTerm?.length < 3) {
      shouldNotify = false;
    }

    const skipQuantity = page ? (Number(page) - 1) * limit : skip;

    const elevateListParams = generateElevateListParams(rest);

    const searchPageResponse = await elevateApi.query.searchPage({
      q: searchTerm,
      limit,
      skip: skipQuantity,
      ...baseSettings,
      notify: shouldNotify,
      origin,
      ...elevateListParams,
      // ...elevateStoresParam
    },
   {
      contentLists: [{ id: 'all-content' }]
    });
     
    return searchPageResponse;
  };

    const getProductPageData = async ({ productKey}) => {
        const pdp_recommendation_lists = [
           {
            id:'alternative-products',
            algorithm: RECOMMENDATION_ALGORITHMS.alternatives 
          },
           {
            id: 'upsell-products',
            algorithm: RECOMMENDATION_ALGORITHMS.upsell
          },
          {
            id: 'style-with',
            algorithm: RECOMMENDATION_ALGORITHMS.styleWith 
          },
           {
            id: 'recently-viewed',
            algorithm: RECOMMENDATION_ALGORITHMS.recentlyViewed
          },
        ]

        if (!elevateApi) {
          // SSR / missing elevate init: keep Elevate optional
          return null;
        }
      const productPageResponse = await elevateApi.query.productPage({
        productKey,
        ...baseSettings,
        ...elevateStoresParam
      },{
        productGroup: {
          include: true
        },
        recommendationLists: pdp_recommendation_lists
      });
      
      return productPageResponse;
    };


  const getAddToCartPopupProducts = async ({
    pipedCartProductKeys,
    variantKey,
    limit = 4
  }) => {
    if (!elevateApi || !variantKey) {
      // SSR / missing elevate init: keep Elevate optional
      return null;
    }

    const cartKeys =
      typeof pipedCartProductKeys === 'string' &&
      pipedCartProductKeys.length > 0
        ? pipedCartProductKeys.split('|').filter(Boolean)
        : Array.isArray(pipedCartProductKeys)
          ? pipedCartProductKeys.filter(Boolean)
          : [];

    const addToCartPopupProductsResponse = await elevateApi.query.addToCartPopup(
      {
        variantKey,
        ...(cartKeys.length ? { cart: cartKeys } : {}),
        ...baseSettings,
        ...elevateStoresParam
      },
      [
        {
          id: 'add-to-cart-recs',
          algorithm: 'ADD_TO_CART_RECS',
          limit
        }
      ]
    );

    return addToCartPopupProductsResponse;
  };

  const getCartPageData = async ({
    cartProductKeys,
    limit = 4
  }) => {
    if (!elevateApi) {
      // SSR / missing elevate init: keep Elevate optional
      return null;
    }

    const cartPageResponse = await elevateApi.query.cartPage({
      ...baseSettings,
      cart:cartProductKeys,
      limit,
      ...elevateStoresParam
    }, 
   { recommendationLists: [{
         id: 'cart',
         algorithm: 'CART',
         productRules: cartRecProductRules(selectedChannel?.currency?.name)
      }]});
    return cartPageResponse;
  };

  
  /* Post functions */

  /**
   * Send a notification of a specific type to the Elevate API.
   *
   * @param {string} type - The type of notification to send (e.g., "click", "addToCart", "addFavorite", "removeFavorite").
   * @param {any} ticket - The ticket or payload required by the notify endpoint.
   * @returns {Promise<null|void>} Returns null if elevateApi is not initialized, otherwise resolves after sending the notification.
   */
  const notification = async (type, ticket) => {
    if (!elevateApi) {
      // SSR / missing elevate init: keep Elevate optional
      return null;
    }
    await elevateApi.notify[type](ticket);
  }



  /* Helper functions */

  const queryStringToObject = queryString => {
    const params = new URLSearchParams(queryString);
    const data = {};

    // Iterate through the parameters and populate the object
    for (const [key, value] of params) {
      // Convert pipes back to commas and split into arrays for facet parameters
      if (key.startsWith('f.') && value.includes('|')) {
        data[key] = value.split('|');
      } else {
        data[key] = value;
      }
    }

    return data;
  };
  
  const objectToQueryString = obj => {
  const queryParts = Object.entries(obj).map(([key, value]) => {
    // URL encode the key and value first
    const encodedKey = encodeURIComponent(key);

    // Handle arrays by joining with commas (which will be converted to pipes)
    let encodedValue;
    if (Array.isArray(value)) {
      encodedValue = encodeURIComponent(value.join(','));
    } else {
      encodedValue = encodeURIComponent(value);
    }
    return `${encodedKey}=${encodedValue}`;
  });

  const queryString = queryParts
    .join('&')
    // Replace URL-encoded commas with pipes after building the query string
    .replace(/%2C/g, '|');

  return queryString;
};

const pushFacetLocation = (history, location, search) => {
  history.push({
    pathname: location?.pathname ?? history?.location?.pathname,
    search,
    hash: location?.hash ?? history?.location?.hash ?? ''
  });
};

const handleRangeFacetChanged = (min, max, id, originalTerm) => {
    const params = queryStringToObject(location.search);

    // Only set params if they differ from the original range
    if (min) {
      params[`f.${id}.min`] = min;
    } else {
      delete params[`f.${id}.min`];
    }

    if (max) {
      params[`f.${id}.max`] = max;
    } else {
      delete params[`f.${id}.max`];
    }

    // Remove page parameter when changing facets
    delete params.page;

    if (originalTerm) {
      params.term = originalTerm;
    }

    pushFacetLocation(history, location, objectToQueryString(params));
}

const handleFacetClick = (facetId, valueId, isSelected, originalTerm, facetType) => {
    const currentParams = queryStringToObject(location.search);
    const facetKey = `f.${facetId}`;
    const newParams = { ...currentParams };

    if (originalTerm) {
      newParams.term = originalTerm;
    }

    // Checkbox facets are a single boolean. Elevate requires an explicit
    // true/false — omitting the param keeps the configured default (often on).
    if (facetType === 'CHECKBOX') {
      newParams[facetKey] = isSelected ? 'false' : 'true';
    } else {
      let currentValues = currentParams[facetKey] || [];
      if (typeof currentValues === 'string') {
        currentValues = [currentValues];
      }

      if (isSelected) {
        currentValues = currentValues.filter(v => v !== valueId);
      } else {
        currentValues = [...currentValues, valueId];
      }

      if (currentValues.length === 0) {
        delete newParams[facetKey];
      } else {
        newParams[facetKey] = currentValues;
      }
    }

    delete newParams.page;

    pushFacetLocation(history, location, objectToQueryString(newParams));
  };

  const removeFacetValue = (facetId, valueId) => {
    const currentParams = queryStringToObject(location.search);
    const newParams = { ...currentParams };

    // Custom stock filters live as c.online / c.store (not f.*)
    if (
      facetId === CUSTOM_STORE_FACET_KEYS.ONLINE ||
      facetId === CUSTOM_STORE_FACET_KEYS.STORE
    ) {
      if (facetId === CUSTOM_STORE_FACET_KEYS.ONLINE) {
        newParams[facetId] = false;
      } else {
        delete newParams[facetId];
      }
      delete newParams.page;
      pushFacetLocation(history, location, objectToQueryString(newParams));
      return;
    }

    const facetKey = `f.${facetId}`;

    if (valueId === 'true' || valueId === 'false') {
      newParams[facetKey] = 'false';
    } else {
      let currentValues = currentParams[facetKey] || [];
      if (typeof currentValues === 'string') {
        currentValues = [currentValues];
      }

      currentValues = currentValues.filter(v => v !== valueId);

      if (currentValues.length === 0) {
        delete newParams[facetKey];
      } else {
        newParams[facetKey] = currentValues;
      }
    }

    delete newParams.page;

    pushFacetLocation(history, location, objectToQueryString(newParams));
  };

  // Extract active facets from URL
  const getActiveFacets = facets => {
    if (!facets || !history?.location?.search) {
      return [];
    }

    const params = queryStringToObject(history?.location?.search);
    const activeFacets = [];

    Object.keys(params).forEach(key => {
      if (key.startsWith('f.')) {
        const facetId = key.replace('f.', '');
        const facet = facets?.find(f => f.id === facetId);

        if (facet) {
          let values = params[key];
          if (typeof values === 'string') {
            values = [values];
          }

          values.forEach(valueId => {
            // Find the label for this value
            let valueLabel = valueId;

            if (facet.type === 'CHECKBOX') {
              if (valueId !== 'true') return;
              valueLabel = facet.label;
            } else if (facet.values) {
              const value = facet.values.find(v => v.id === valueId);
              if (value) {
                valueLabel = value.label;
              }
            } else if (facet.sizeTypes) {
              // Handle size facets
              for (const sizeType of facet.sizeTypes) {
                for (const format of sizeType.formats || []) {
                  const value = format.values?.find(v => v.id === valueId);
                  if (value) {
                    valueLabel = value.label;
                    break;
                  }
                }
              }
            }

            activeFacets.push({
              facetId,
              facetLabel: facet.label,
              valueId,
              valueLabel,
              key: `${facetId}-${valueId}`
            });
          });
        }
      }
    });

    const onlineValue = params[CUSTOM_STORE_FACET_KEYS.ONLINE];
    if (
      Object.prototype.hasOwnProperty.call(
        params,
        CUSTOM_STORE_FACET_KEYS.ONLINE
      ) &&
      onlineValue !== false &&
      onlineValue !== 'false'
    ) {
      activeFacets.push({
        facetId: CUSTOM_STORE_FACET_KEYS.ONLINE,
        facetLabel: 'In stock online',
        valueId: 'true',
        valueLabel: 'Online',
        key: `${CUSTOM_STORE_FACET_KEYS.ONLINE}-true`
      });
    }

    const storeId = params[CUSTOM_STORE_FACET_KEYS.STORE];
    if (storeId) {
      const store = getAllStores()?.find(
        s => String(s.id) === String(storeId)
      );
      const storeLabel = store?.name || String(storeId);

      activeFacets.push({
        facetId: CUSTOM_STORE_FACET_KEYS.STORE,
        facetLabel: 'In stock at store',
        valueId: String(storeId),
        valueLabel: storeLabel,
        key: `${CUSTOM_STORE_FACET_KEYS.STORE}-${storeId}`
      });
    }

    return activeFacets;
  };

  const clearAllFacets = () => {
    const currentParams = queryStringToObject(history?.location?.search);
    const newParams = {};

    // Keep non-facet parameters (except custom stock filters)
    Object.keys(currentParams).forEach(key => {
      if (
        key.startsWith('f.') ||
        key === CUSTOM_STORE_FACET_KEYS.ONLINE ||
        key === CUSTOM_STORE_FACET_KEYS.STORE
      ) {
        return;
      }
      newParams[key] = currentParams[key];
    });

    delete newParams.page;

    pushFacetLocation(
      history,
      history.location,
      objectToQueryString(newParams)
    );
  };

  const handleSortClick = sortId => {

    const params = queryStringToObject(history?.location?.search);
    params.sort = sortId;
    delete params.page; // Reset pagination
    pushFacetLocation(
      history,
      history.location,
      objectToQueryString(params)
    );
  };

  const getElevateProductByKeyFromProductGroup = (productGroup, productKey) => {
    return productGroup?.products?.find(product => product?.key === productKey);
  }

  const groupContentListByType = contentLists => {
    const groupedContent = {};
    contentLists?.[0]?.items?.forEach(item => {
      if (!groupedContent[item.type]) {
        groupedContent[item.type] = [];
      }
      groupedContent[item.type].push(item);
    });
    return groupedContent;
  };

  const getCustomerKey = useCallback(() => {
    if (!elevateSession) return null;
    return elevateSession().customerKey;
  }, [elevateSession]);

  const updateCustomerKey = useCallback(customerKey => {
    if (!elevateSession) return;

    const { customerKey: currentKey } = elevateSession();
    const { action, key, previousKey } = resolveCustomerKeyAction(
      currentKey,
      customerKey
    );

    logElevateCustomerKey('updateCustomerKey', {
      action,
      currentKey,
      incomingKey: customerKey ?? null,
      previousKey
    });

    if (action === CUSTOMER_KEY_ACTION.CREATE_ANONYMOUS) {
      const newKey = crypto.randomUUID();
      elevateSession.updateCustomerKey(newKey);
      logElevateCustomerKey('created anonymous key', { newKey });
      return;
    }
    if (
      action === CUSTOMER_KEY_ACTION.SET_INCOMING ||
      action === CUSTOMER_KEY_ACTION.SYNC_ENGAGE
    ) {
      elevateSession.updateCustomerKey(key);
    }
  }, [elevateSession]);

  const resetCustomerKey = useCallback(() => {
    if (!elevateSession) return;
    const { customerKey: previousKey } = elevateSession();
    elevateSession.reset();
    const { customerKey: newKey } = elevateSession();
    logElevateCustomerKey('resetCustomerKey (logout)', { previousKey, newKey });
  }, [elevateSession]);
  

 //Data fetching functions
  const fetchFunctions = useMemo(
    () => ({  
      getLandingPageData, 
      getAutoCompleteData,
      getSearchPageData,
      getLandingPageRecommendationsData,
      getProductPageData,
      getAddToCartPopupProducts,
      getCartPageData
    }),
    [elevateApi, elevateStoresParam, market, locale, touchpoint]
  );


  const postFunctions = useMemo(
    () => ({
      notification,
    }),
    []
  );

  //Helper functions
  const helpers= useMemo(
    () => ({
      queryStringToObject,
      objectToQueryString,
      SEARCH_ORIGINS: ELEVATE_SEARCH_ORIGINS,
      PER_PAGE: ElevateDefaultSettings.PER_PAGE,
      RECOMMENDATION_ALGORITHMS, 
      NOTIFICATION_TYPES,
      handleFacetClick,
      removeFacetValue,
      getActiveFacets,
      handleRangeFacetChanged,
      clearAllFacets,
      handleSortClick,
      getElevateProductByKeyFromProductGroup,
      groupContentListByType, 
      getCustomerKey,
      updateCustomerKey, 
      resetCustomerKey
    }),
    []
  );

  const variables = useMemo(
    () => ({
      listFacetStoreId
    }),
    [listFacetStoreId]
  );

  return (
    <ElevateContext.Provider value={{...fetchFunctions, ...postFunctions, ...helpers, ...variables}}>{children}<ElevateCustomerHandler /></ElevateContext.Provider>
  );
};


export const useElevate = () => {
  const context = useContext(ElevateContext);
  if (!context) {
    throw new Error('useElevate must be used within an ElevateProvider');
  }
  return context;
};

